import { useEffect } from 'react'
import {
  HTML_LANG,
  SUPPORTED_LOCALES,
  pathInLocale,
  useTranslation,
  type UiLocale,
} from '@lb/i18n'

import { env } from '../../env'

/**
 * Per-route metadata: title, description, canonical, Open Graph, `hreflang`.
 *
 * ── What this does and does not fix ───────────────────────────────────────
 * Tags written by JavaScript are read by Google, which executes JS. They are
 * **not** read by Facebook, iMessage, Slack, WhatsApp or LinkedIn, whose
 * crawlers fetch the HTML and stop. So this fixes search and does not fix
 * shared-link cards; those need HTML that already contains the tags, which is
 * the prerender decision in `Phase_8_Plan.md` §1.2.
 *
 * Shipping it anyway is not wasted: prerendering works by rendering these very
 * components and snapshotting the result, so this is the thing being
 * prerendered rather than a stopgap to be replaced.
 *
 * ── Why the tags are managed here rather than by a helmet library ─────────
 * There are eight of them and they all key off one route. A head-management
 * dependency would add a provider, a render-phase side-effect model and a
 * server-rendering story we do not have, to do what a `useEffect` and a
 * `querySelector` do.
 */

export interface SeoProps {
  /** i18n key for the page title, without the brand suffix. */
  titleKey: string
  /** i18n key for the meta description. Required — a page without one is a gap. */
  descriptionKey: string
  /** Absolute path this page canonically lives at. */
  path: string
  /** `false` for anything behind the session. */
  indexable?: boolean
}

function upsertMeta(selector: string, attrs: Record<string, string>) {
  let node = document.head.querySelector<HTMLMetaElement>(selector)
  if (!node) {
    node = document.createElement('meta')
    document.head.appendChild(node)
  }
  for (const [key, value] of Object.entries(attrs)) node.setAttribute(key, value)
}

function upsertLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`
  let node = document.head.querySelector<HTMLLinkElement>(selector)
  if (!node) {
    node = document.createElement('link')
    node.rel = rel
    if (hreflang) node.hreflang = hreflang
    document.head.appendChild(node)
  }
  node.href = href
}

export function Seo({ titleKey, descriptionKey, path, indexable = false }: SeoProps) {
  const { t, i18n } = useTranslation()
  const language: UiLocale = i18n.language === 'en' ? 'en' : 'fr'

  useEffect(() => {
    const brand = t('brand.name')
    const title = `${t(titleKey)} · ${brand}`
    const description = t(descriptionKey)
    // Configured origin first — see `env.ts`. Falls back to the live one for
    // a normal dev run, where there is no public origin to state.
    const origin = env.VITE_SITE_URL ?? window.location.origin
    const url = `${origin}${pathInLocale(path, language)}`

    document.title = title
    upsertMeta('meta[name="description"]', { name: 'description', content: description })

    /*
     * `noindex` on everything behind the session, and it is not paranoia: the
     * app renders a member's own name, their children and their community
     * posts. A crawler that reached one and indexed it would be the worst
     * possible disclosure, and the cost of the tag is nothing.
     */
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: indexable ? 'index,follow' : 'noindex,nofollow',
    })

    upsertLink('canonical', url)

    // Open Graph — what a shared link renders as, once prerendering lands.
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title })
    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: description,
    })
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' })
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url })
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: brand })
    upsertMeta('meta[property="og:locale"]', {
      property: 'og:locale',
      content: HTML_LANG[language].replace('-', '_'),
    })
    upsertMeta('meta[property="og:image"]', {
      property: 'og:image',
      content: `${origin}/icon-512.png`,
    })

    // `summary_large_image` needs a 1200×630; until there is brand artwork for
    // it, `summary` is the honest card — a square icon in a wide frame looks
    // broken, and a wrong card is worse than a small one.
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary' })

    /*
     * `hreflang` points at real, prerendered paths — `/` and `/en/`.
     *
     * These were `?lang=` query strings, which a static host cannot serve
     * different HTML for: every variant returned the same file, so a crawler
     * following the English alternate got French markup. A path is a file.
     */
    for (const locale of SUPPORTED_LOCALES) {
      upsertLink('alternate', `${origin}${pathInLocale(path, locale)}`, HTML_LANG[locale])
    }
    upsertLink('alternate', `${origin}${pathInLocale(path, 'fr')}`, 'x-default')
  }, [t, titleKey, descriptionKey, path, indexable, language])

  return null
}

/**
 * JSON-LD for the organisation, on the public landing only.
 *
 * ── Why this is worth the twenty lines ────────────────────────────────────
 * It is what lets a search result show the brand rather than a bare URL, and
 * it is the one structured-data type this product can assert truthfully today.
 * `Course` and `Article` schemas would be far more valuable — they produce rich
 * results for exactly the content parents search for — but they need the public
 * teaser surface that is still with the operator. Claiming them now would mean
 * marking up content a crawler cannot fetch, which is the kind of structured
 * data that gets a site penalised rather than ranked.
 *
 * Injected as a real `<script type="application/ld+json">` in `<head>` rather
 * than rendered into the body, because that is where crawlers look and because
 * React would otherwise escape the JSON.
 */
export function OrganizationSchema() {
  const { t } = useTranslation()

  useEffect(() => {
    const origin = env.VITE_SITE_URL ?? window.location.origin
    const id = 'lb-org-schema'
    let node = document.getElementById(id) as HTMLScriptElement | null
    if (!node) {
      node = document.createElement('script')
      node.id = id
      node.type = 'application/ld+json'
      document.head.appendChild(node)
    }

    node.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: t('brand.name'),
      description: t('seo.landingDescription'),
      url: origin,
      logo: `${origin}/icon-512.png`,
      // Named because they are the product's authorship, and the design spec
      // treats them as such rather than as staff.
      founder: [
        { '@type': 'Person', name: 'Krysta Letto' },
        { '@type': 'Person', name: 'Sarah Hamel' },
      ],
      areaServed: { '@type': 'AdministrativeArea', name: 'Québec' },
      inLanguage: ['fr-CA', 'en-CA'],
    })

    return () => node?.remove()
  }, [t])

  return null
}

/**
 * `noindex` for the whole authenticated app, asserted once.
 *
 * ── Why this is not per-route ─────────────────────────────────────────────
 * Wiring `<Seo>` into each of the twenty-odd member routes would work and
 * would be wrong: it makes "is this page indexable?" a decision somebody makes
 * twenty times, and the failure mode of forgetting once is a member's own
 * space — their name, their children, their community posts — in a search
 * index. Rendered inside `AppShell`, which only mounts for a signed-in member,
 * the answer is structural: if you are inside the app, you are not indexable.
 *
 * `robots.txt` says the same thing, and both are needed. The file is a request
 * a crawler may ignore; the tag is what stops a page that was reached anyway
 * from being indexed.
 *
 * The TITLE is deliberately not set here — `RouteAnnouncer` derives it from the
 * page's own `<h1>`, which is truer than a second table of names that would
 * have to be kept in step with the headings.
 */
export function NoIndex() {
  useEffect(() => {
    upsertMeta('meta[name="robots"]', { name: 'robots', content: 'noindex,nofollow' })
    return () => {
      // Back to the public default when the shell unmounts — signing out lands
      // on the landing page, which IS indexable.
      upsertMeta('meta[name="robots"]', { name: 'robots', content: 'index,follow' })
    }
  }, [])

  return null
}
